import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { VariantMapAside } from '../models/variant-map-aside';
import { ValueInputsDisabled } from '../components/events-content/models/event-content.model';
import { WhatsAppResponse } from 'src/app/events/models/interfaces';
import { MultimediaService } from 'src/app/multiview/services/multimedia.service';
import { DriverIdentificationService } from 'src/app/drivers/driver-identification/services/driver-identification.service';
import { EventService } from 'src/app/events/services/event.service';
import { PermissionService } from 'src/app/permiss/permission.service';

@Injectable({
  providedIn: 'root',
})
export class MapAsideService {
  private isHiddenSubject = new BehaviorSubject<boolean>(false);
  isHidden$ = this.isHiddenSubject.asObservable();

  private variantSubject = new BehaviorSubject<VariantMapAside>('not-found');
  variant$ = this.variantSubject.asObservable();

  private dataSubject = new BehaviorSubject<any>(null);
  data$ = this.dataSubject.asObservable();

  private auxDisabledSubject = new BehaviorSubject<boolean>(false);
  auxDisabled$ = this.auxDisabledSubject.asObservable();

  private errorServerSubject = new BehaviorSubject<boolean>(false);
  errorServer$ = this.errorServerSubject.asObservable();

  private valueInputsDisabledSubject =
    new BehaviorSubject<ValueInputsDisabled | null>(null);
  valueInputsDisabled$ = this.valueInputsDisabledSubject.asObservable();

  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();

  private isMaximizedSubject = new BehaviorSubject<boolean>(false);
  isMaximized$ = this.isMaximizedSubject.asObservable();

  constructor(
    private multimediaService: MultimediaService,
    private driverIdentificationService: DriverIdentificationService,
    private eventService: EventService,
    private permissionService: PermissionService
  ) {}

  toggle(variant: VariantMapAside) {
    this.isHiddenSubject.next(!this.isHiddenSubject.value);
    if (this.isHiddenSubject.value) {
      this.variantSubject.next(variant);
    }
    this.isLoadingSubject.next(false);
  }

  show(variant: VariantMapAside, data: any) {
    console.log('show aside service', data);
    //this.deleteMultimediaFromPreviousEvent();

    this.permissionService.hasPermission("validate_event_driver").then(hasPermission => {
      if(data.uuid_event && hasPermission){
        if(data.evaluated == 1){
          this.driverIdentificationService.close();
        } else {
          if(this.driverIdentificationService.canIdentifyDriverEvent(data.evento || "")){
            if(this.eventService.config.autoOpenDriverPanel){
              this.driverIdentificationService.reset(data.uuid_event);
              this.driverIdentificationService.openDriverIdentificationPanel(data)
            } else {
              this.driverIdentificationService.close();
              this.driverIdentificationService.selectDriverById(Number(data.driver_id) || 0)
            }
          } else {
            this.driverIdentificationService.close();
          }
        }
      }
    });
    this.variantSubject.next(variant);
    this.isHiddenSubject.next(true);
    this.dataSubject.next(data);
  }

  getData() {
    return this.dataSubject.getValue();
  }

  deleteMultimediaFromPreviousEvent() {
    const current = this.dataSubject.getValue();
    if (current && current.evaluated == 1) {
      console.log('aside eliminar multimedia de evento anterior');
      this.multimediaService.deleteMultimediaFromEvent(current.uuid_event);
    } else {
      console.log('aside eliminar multimedia de evento anterior no es posible');
    }
  }

  markEventAsEvaluated() {
    const current = this.dataSubject.getValue();
    if (!current.evaluated) {
      this.dataSubject.next({
        ...current,
        evaluated: 1,
      });
    }
  }

  hide() {
    console.log('hide aside service');
    const current = this.dataSubject.getValue();
    //this.deleteMultimediaFromPreviousEvent();
    this.driverIdentificationService.reset(); // resetear driver seleccionado al mostrar el aside

    this.isHiddenSubject.next(false);
  }

  getState(): boolean {
    return this.isHiddenSubject.value;
  }

  setValueAuxDisabled(
    state: boolean,
    values: ValueInputsDisabled | null,
    error?: boolean
  ) {
    this.auxDisabledSubject.next(state);
    this.valueInputsDisabledSubject.next(values);
    this.errorServerSubject.next(error ?? false);
  }

  // codigo en decadencia
  private submitFormEventContentSubject = new BehaviorSubject<{
    body: any | undefined;
    event: any | undefined;
  }>({ body: undefined, event: undefined });
  submitFormEventContent$ = this.submitFormEventContentSubject.asObservable();

  setSubmitFormEventContent(body: any, event: any) {
    this.submitFormEventContentSubject.next({ body, event });
  }

  setIsLoading(state: boolean) {
    this.isLoadingSubject.next(state);
  }

  // mas decadencia
  private sendEvaluationByWhatsappSubject = new BehaviorSubject<boolean>(false);
  sendEvaluationByWhatsapp$ =
    this.sendEvaluationByWhatsappSubject.asObservable();

  setSendEvaluationByWhatsapp(state: boolean) {
    this.sendEvaluationByWhatsappSubject.next(state);
  }

  private sendExternalSubject = new BehaviorSubject<boolean>(false);
  sendExternal$ = this.sendExternalSubject.asObservable();

  setSendExternal(state: boolean) {
    this.sendExternalSubject.next(state);
  }

  // maximice multimedia
  setIsMaximized(state: boolean) {
    this.isMaximizedSubject.next(state);
  }

  private messagesSentByWhatsAppSubject = new BehaviorSubject<
    WhatsAppResponse[] | null
  >(null);
  messagesSentByWhatsApp$ = this.messagesSentByWhatsAppSubject.asObservable();

  setMessagesSentByWhatsApp(messages: WhatsAppResponse[]) {
    this.messagesSentByWhatsAppSubject.next(messages);
  }

  private getNextEventSubject = new BehaviorSubject<void>(undefined);
  getNextEvent = this.getNextEventSubject.asObservable();

  getNextEventPublic() {
    this.getNextEventSubject.next();
  }

  private getPreviousEventSubject = new BehaviorSubject<void>(undefined);
  getPreviousEvent = this.getPreviousEventSubject.asObservable();

  getPreviousEventPublic() {
    this.getPreviousEventSubject.next();
  }
}
